import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Quote, TrendingUp } from 'lucide-react';

interface SuccessStoryCardProps {
  name: string;
  role: string;
  image?: string;
  story: string;
  beforeStat?: string;
  afterStat?: string;
  highlight?: string;
  className?: string;
}

const SuccessStoryCard: React.FC<SuccessStoryCardProps> = ({
  name,
  role,
  image,
  story,
  beforeStat,
  afterStat,
  highlight,
  className = ''
}) => {
  return (
    <Card className={`hover:shadow-xl transition-all ${className}`}>
      <CardContent className="p-6 space-y-4">
        {/* Quote Icon */}
        <Quote className="w-8 h-8 text-primary/40" />
        
        {/* Story */}
        <p className="text-muted-foreground italic leading-relaxed">
          "{story}"
        </p>
        
        {/* Stats */}
        {(beforeStat || afterStat) && (
          <div className="flex items-center gap-4 py-4 border-y border-border">
            {beforeStat && (
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Before</p>
                <p className="text-lg font-semibold">{beforeStat}</p>
              </div>
            )}
            {beforeStat && afterStat && (
              <TrendingUp className="w-5 h-5 text-green-500" />
            )}
            {afterStat && (
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">After</p>
                <p className="text-lg font-semibold text-green-600">{afterStat}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Highlight */}
        {highlight && (
          <Badge className="text-sm py-2 px-4">
            {highlight}
          </Badge>
        )}
        
        {/* Author */}
        <div className="flex items-center gap-3 pt-2">
          {image ? (
            <img 
              src={image} 
              alt={name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-semibold">
              {name.charAt(0)}
            </div>
          )}
          <div>
            <p className="font-semibold">{name}</p>
            <p className="text-sm text-muted-foreground">{role}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuccessStoryCard;
